/**
 * updateLine — the words for each update stage, in one place, so the banner
 * and the on-open screen say exactly the same thing (job #759).
 *
 * `busy` is true only while a stage is genuinely in motion; `fraction` is set
 * only when the file count is actually known. No count, no bar — never a
 * progress bar that is guessing.
 */
import type { UpdatePhase, UpdateFailure } from './useAppUpdate'

export interface UpdateLine {
  text: string
  busy: boolean
  fraction: number | null
}

type T = (key: string, fallback?: string) => string

/** A build id as a learner can read it: the 7-char short hash. */
export function shortBuild(build: string | null | undefined): string {
  return (build ?? '').slice(0, 7)
}

export function updateLine(t: T, s: {
  phase: UpdatePhase
  progress: { done: number; total: number } | null
  target: string | null
  failure: UpdateFailure | null
  heldForPause?: boolean
}): UpdateLine {
  const build = shortBuild(s.target)
  switch (s.phase) {
    case 'checking':
      return { text: t('update.checking'), busy: true, fraction: null }
    case 'current':
      return { text: t('update.current').replace('{build}', build), busy: false, fraction: null }
    case 'found':
      return { text: t('update.found'), busy: true, fraction: null }
    case 'downloading': {
      const p = s.progress
      if (p && p.total > 0) {
        return {
          text: t('update.downloadingCount')
            .replace('{done}', String(p.done))
            .replace('{total}', String(p.total)),
          busy: true,
          fraction: Math.max(0, Math.min(1, p.done / p.total)),
        }
      }
      return { text: t('update.downloading'), busy: true, fraction: null }
    }
    case 'ready':
      return { text: s.heldForPause ? t('update.readyHeld') : t('update.ready'), busy: false, fraction: null }
    case 'restarting':
      return { text: t('update.restarting'), busy: true, fraction: null }
    case 'wedged':
      return { text: t('update.wedged'), busy: false, fraction: null }
    case 'updated':
      return { text: t('update.updated').replace('{build}', build), busy: false, fraction: null }
    case 'failed': {
      const key = s.failure === 'offline' ? 'update.failedOffline'
        : s.failure === 'stalled' ? 'update.failedStalled'
        : s.failure === 'still-old' ? 'update.failedStillOld'
        : 'update.failedDownload'
      return { text: t(key), busy: false, fraction: null }
    }
    default:
      return { text: '', busy: false, fraction: null }
  }
}
